const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "db3doymln";
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "bpvs6db0";

// Compress image using canvas before uploading
// Target: max 800px (mobile-first), WebP if supported, ~150KB
async function compressImage(file) {
  return new Promise((resolve) => {
    // Skip if already tiny
    if (file.size < 150 * 1024) { resolve(file); return; }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);

      // Max 800px on longest side — enough for any phone screen @2x
      const MAX = 800;
      let { width, height } = img;
      if (width > height) {
        if (width > MAX) { height = Math.round(height * MAX / width); width = MAX; }
      } else {
        if (height > MAX) { width = Math.round(width * MAX / height); height = MAX; }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Use WebP if browser supports it (50% smaller than JPEG at same quality)
      const supportsWebP = canvas.toDataURL("image/webp").startsWith("data:image/webp");
      const outputType = supportsWebP ? "image/webp" : "image/jpeg";
      const quality = supportsWebP ? 0.82 : 0.78;

      canvas.toBlob(
        (blob) => {
          // Only use compressed if it's actually smaller
          resolve(blob && blob.size < file.size ? blob : file);
        },
        outputType,
        quality
      );
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });
}

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export async function uploadImage(file) {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error("Only JPEG, PNG, WebP, or GIF images are allowed.");
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("Image must be smaller than 5MB.");
  }

  const compressed = await compressImage(file);

  const formData = new FormData();
  formData.append("file", compressed);
  formData.append("upload_preset", UPLOAD_PRESET);

  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) throw new Error("Image upload failed");
  const data = await res.json();

  // Validate returned URL is from our Cloudinary account
  if (!data.secure_url || !data.secure_url.includes(`res.cloudinary.com/${CLOUD_NAME}`)) {
    throw new Error("Upload response invalid.");
  }

  // Inject Cloudinary auto-optimization: serve correct format + quality per device
  // e.g. .../upload/v123/abc.jpg → .../upload/f_auto,q_auto,w_800/v123/abc.jpg
  const optimizedUrl = data.secure_url.replace(
    "/upload/",
    "/upload/f_auto,q_auto,w_800/"
  );

  return optimizedUrl;
}
